/**
 * Authentication utilities for identity server integration
 */

import { getEnv } from "@/config/env";
import { getLocale } from "@/utils/locale-store";

interface BuildLoginUrlOptions {
  /** Frontend path to redirect to after login (e.g. "/sv/checkout"). */
  returnPath?: string;
  /** When true, the identity server shows a "Continue as Guest" option. */
  allowGuest?: boolean;
}

/**
 * Builds the login URL with the correct return URL chain.
 *
 * Two flows, chosen per site by the `ssoLoginEnabled` config switch:
 *
 * Legacy (flag off) — both authenticated and guest flows go through the backend:
 * 1. Browser → identity server (login UI)
 * 2. Identity server → backend auth endpoint (with token or guestEmail)
 * 3. Backend → frontend page (after setting session cookie or forwarding guest email)
 *
 * Natlink SSO (flag on):
 * 1. Browser → backend `sso/start` endpoint
 * 2. Backend → SSO login page, then back to the backend, which mints the
 *    signed state/nonce and receives the token itself
 * 3. Backend → frontend page (after setting session cookie)
 *
 * The frontend only ever points the browser at `sso/start` — it never talks
 * to the SSO provider directly. The SSO protocol (state, nonce, and the
 * registered redirect URI) is owned by the backend because the redirect URI
 * must match the SSO's registry exactly, so it can't carry per-request data;
 * the backend mints the signed state that carries the return path instead.
 * There is no guest option on this path (the shop offers guest checkout on
 * its own choice screen, CheckoutLoginChoice) and the SSO login page picks its locale
 * from the browser, so neither `allowGuest` nor `lang` is forwarded.
 */
export function buildLoginUrl(options: BuildLoginUrlOptions = {}): string {
  const {
    apiBaseUrl,
    siteId,
    identityServerUrl,
    identityServerClientId,
    ssoLoginEnabled,
  } = getEnv();
  const { returnPath, allowGuest } = options;

  if (ssoLoginEnabled) {
    let url = `${apiBaseUrl}/auth/${siteId}/sso/start`;
    if (returnPath) {
      url += `?returnUrl=${encodeURIComponent(returnPath)}`;
    }
    return url;
  }

  // Always route through the backend auth endpoint
  let returnUrl = `${apiBaseUrl}/auth/${siteId}/login`;
  if (returnPath) {
    returnUrl += `?returnUrl=${encodeURIComponent(returnPath)}`;
  }

  // The locale store keeps a full locale (e.g. "sv-SE"); the identity server
  // wants just the language code (en/fr/de/da/fi/no).
  const { language } = getLocale();
  const lang = (language.split("-")[0] ?? language).toLowerCase();

  // Build identity server URL
  let url = `${identityServerUrl}/?client_id=${encodeURIComponent(identityServerClientId)}&return_url=${encodeURIComponent(returnUrl)}&lang=${encodeURIComponent(lang)}`;

  if (allowGuest) {
    url += "&AllowGuest=true";
  }

  return url;
}

/**
 * Extracts the guest email from the current URL query parameters.
 * The identity server appends `guestEmail={email}` when a guest continues to checkout.
 */
export function getGuestEmailFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("guestEmail");
}

/**
 * Backend signout endpoint path. Lives on the v1 API even though the rest of
 * the app uses v2 (no v2 logout endpoint exists). Update here if the path moves.
 */
const SIGNOUT_PATH = "/api/v1/d2cauth/signout";

/**
 * Reduces a URL to a comparable path: no origin, no query, no fragment, no
 * trailing slash, lowercased. Absolute and relative forms of the same page
 * therefore compare equal.
 */
function toComparablePath(url: string): string {
  let path = url;
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      // Malformed — fall through and compare the raw string.
    }
  }
  path = path.split("?")[0]?.split("#")[0] ?? path;
  return path.replace(/\/+$/, "").toLowerCase();
}

function isSameOrigin(url: string, other: string): boolean {
  try {
    return (
      new URL(url).origin.toLowerCase() === new URL(other).origin.toLowerCase()
    );
  } catch {
    return false;
  }
}

/**
 * True when a CMS-authored link leads to the same place as the header's own
 * account control: the identity server, the backend's SSO start endpoint, or
 * one of the login / my-account pages.
 *
 * Editors have pointed the free-form topbar CTA at the login page, which then
 * renders as a second account control with a hard-coded "Login" label — it
 * cannot follow the signed-in state the way the real control does. Callers use
 * this to drop such a duplicate rather than show two contradicting labels.
 */
export function isAccountDestination(
  href: string | null | undefined,
  accountUrls: readonly (string | null | undefined)[]
): boolean {
  if (!href) return false;

  const { identityServerUrl } = getEnv();
  if (identityServerUrl && isSameOrigin(href, identityServerUrl)) return true;

  const target = toComparablePath(href);
  // A CMS link may point straight at the backend's SSO start endpoint once
  // that site's login link is switched over.
  if (target.endsWith("/sso/start")) return true;
  // An empty path is the site root, which is nobody's account page.
  if (!target) return false;

  return accountUrls.some((url) => !!url && toComparablePath(url) === target);
}

/**
 * Backend signout endpoint. Clears the Umbraco member cookie and redirects
 * to the locale-aware start page.
 */
export function buildSignoutUrl(): string {
  const { apiBaseUrl, siteId } = getEnv();
  const apiOrigin = new URL(apiBaseUrl).origin;
  // Plain browser navigation doesn't carry the SiteId / X-Store-Language headers,
  // so pass both explicitly — otherwise the backend can't tell which site's start
  // page to redirect to (falls back to the default Tracker) nor which language
  // (falls back to English, resetting the user's locale).
  const { language } = getLocale();
  return `${apiOrigin}${SIGNOUT_PATH}?siteId=${encodeURIComponent(siteId)}&culture=${encodeURIComponent(language)}`;
}
